import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

@Injectable({ providedIn: 'root' })
export class CalmeanAuthService {
  private readonly consumerKey = 'ch8UkatNxX7qrR8DWZLgv130EA4a'; // Staging key
  private readonly consumerSecret = 'kGJKhFfcF0JB_NSH27q8Xxig5H8a'; // Staging secret
  private readonly productionConsumerKey = 'dZxKqxVFhguzIAXs1KaU3zU6JJsa';
  private readonly productionConsumerSecret = '7FP6jvE7KztDoqruKFsHKfBHJwMa';
  private readonly stagingLoconConsumerKey = 'vEK7PmzT7TtUf1XZ3FKbW0c9xmEa';
  private readonly stagingLoconConsumerSecret = 'e01FYfJybfvmycJpTB7vhIRhlbUa';
  private readonly productionLoconConsumerKey = '7rSBPZApD4sFulABCf4Vt9nKsKwa';
  private readonly productionLoconConsumerSecret = 'rEVibM6msqhEjUVHsABUnQb13k0a';
  private token: string | null = null;

  constructor(private http: HttpClient) {}

  /**
   * Get active token for API calls
   */
  getActiveToken(): Observable<string> {
    const headers = new HttpHeaders({
      'Authorization': 'Basic ' + btoa(this.getConsumerKey() + ':' + this.getConsumerSecret()),
      'Content-Type': 'application/x-www-form-urlencoded'
    });

    console.log('Getting active token from:', `${this.getApiUrl()}/token`);
    console.log('Authorization header:', 'Basic ' + btoa(this.getConsumerKey() + ':' + this.getConsumerSecret()));

    return this.http.post<any>(`${this.getApiUrl()}/token`, 'grant_type=client_credentials', { headers })
      .pipe(
        tap(response => {
          console.log('Token response:', response);
          // Store the full token object for debugging
          localStorage.setItem('calmeanAppToken', JSON.stringify(response));
        }),
        map(response => {
          this.token = response.access_token;
          return response.access_token;
        }),
        catchError(error => {
          console.error('Error getting active token', error);
          return throwError(() => new Error('Failed to get active token'));
        })
      );
  }

  /**
   * Login with Google token
   */
  loginWithGoogle(email: string, googleToken: string): Observable<any> {
    return this.getActiveToken().pipe(
      switchMap(token => {
        const params = {
          email: email,
          secretKey: googleToken,
          method: 'GOOGLE_AC',
          consumerSecret: this.getConsumerSecret(),
          consumerKey: this.getConsumerKey()
        };

        const headers = new HttpHeaders({
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/x-www-form-urlencoded'
        });

        // Log the request parameters for debugging
        console.log('Login request parameters:', params);
        console.log('Login URL:', `${this.getApiUrl()}/t/calmean.com/account/1.0.0/login`);

        // Send parameters as query params instead of in the body
        return this.http.post<any>(`${this.getApiUrl()}/t/calmean.com/account/1.0.0/login`, '', {
          headers: headers,
          params: params
        });
      }),
      tap(response => {
        console.log('Login response:', response);
      }),
      map(response => {
        if (response.status === 'SUCCESS') {
          console.log('Full login response:', response);

          // Store token in localStorage for future API calls
          localStorage.setItem('calmeanToken', response.account.tokenInfo.access_token);

          // Store the full token info for reference
          if (response.account && response.account.tokenInfo) {
            localStorage.setItem('calmeanTokenInfo', JSON.stringify(response.account.tokenInfo));
          }

          // Store the full account object for reference
          if (response.account) {
            localStorage.setItem('calmeanAccount', JSON.stringify(response.account));
          }

          return response.account;
        } else if (response.status === 'WRONG_RIGHTS') {
          throw new Error('You do not have the required permissions');
        } else if (response.status === 'WRONG_AUTH') {
          throw new Error('Invalid authentication');
        } else {
          throw new Error(response.description || 'Unknown error');
        }
      }),
      catchError(error => {
        console.error('Login error', error);
        return throwError(() => new Error(error.message || 'Failed to login with Google'));
      })
    );
  }

  /**
   * Get consumer key based on environment
   */
  getConsumerKey(): string {
    return environment.production ? this.productionConsumerKey : this.consumerKey;
  }

  /**
   * Get consumer secret based on environment
   */
  getConsumerSecret(): string {
    return environment.production ? this.productionConsumerSecret : this.consumerSecret;
  }

  /**
   * Get API URL from environment
   */
  private getApiUrl(): string {
    return environment.apiUrl;
  }

  /**
   * Get Locon consumer key based on environment
   */
  getConsumerLoconKey(): string {
    return environment.production ? this.productionLoconConsumerKey : this.stagingLoconConsumerKey;
  }

  /**
   * Get Locon consumer secret based on environment
   */
  getConsumerLoconSecret(): string {
    return environment.production ? this.productionLoconConsumerSecret : this.stagingLoconConsumerSecret;
  }
}
